import Card from "components/card";
import Toast from "components/toaster";
import React, { useEffect, useState, useRef } from "react";
import PriceService from "services/price.service";
import ServiceFeeService from "services/serviceFee.service";
import Loader from "../../../assets/img/loader.gif";
import tabCache from "utils/tabCache";

const BoostPricing = () => {
  // Initialize from cache if available
  const [searchInputValue, setSearchInputValue] = useState(tabCache.boostPricing.data?.amount || "");
  const [isEdit, setIsEdit] = useState(false);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(tabCache.boostPricing.firstLoad ? "list" : "");

  // Vendor boost pricing state - initialize from cache if available
  const [vendorBoostInputValue, setVendorBoostInputValue] = useState(tabCache.vendorBoostPricing.data?.amount || "");
  const [isVendorEdit, setIsVendorEdit] = useState(false);
  // Initialize from cache - show loader only on first load
  const [isVendorLoading, setIsVendorLoading] = useState(tabCache.vendorBoostPricing.firstLoad ? "list" : "");

  // Ticket pricing state
  const [ticketPricingInputValue, setTicketPricingInputValue] = useState(
    tabCache.ticketCommission.data?.percentage || ""
  );
  const [isTicketEdit, setIsTicketEdit] = useState(false);
  const [isTicketLoading, setIsTicketLoading] = useState(
    tabCache.ticketCommission.firstLoad ? "list" : ""
  );
  const [serviceFeeExists, setServiceFeeExists] = useState(
    !!tabCache.ticketCommission.data
  );

  const [isAdminEdit, setIsAdminEdit] = useState(false);
  const inputRef = useRef(null);
  const vendorInputRef = useRef(null);
  const ticketInputRef = useRef(null);

  useEffect(() => {
    if (isEdit && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEdit]);

  useEffect(() => {
    if (isVendorEdit && vendorInputRef.current) {
      vendorInputRef.current.focus();
    }
  }, [isVendorEdit]);

  useEffect(() => {
    if (isTicketEdit && ticketInputRef.current) {
      ticketInputRef.current.focus();
    }
  }, [isTicketEdit]);


  const enableEditOnDoubleClick = (setEdit) => {
    setEdit(true);
  };


  const handleSearchInputChange = (e) => {
    const value = e.target.value;

    if (/^\d*\.?\d*$/.test(value)) {
      setSearchInputValue(value);
    }
  };

  const handleVendorBoostInputChange = (e) => {
    const value = e.target.value;

    if (/^\d*\.?\d*$/.test(value)) {
      setVendorBoostInputValue(value);
    }
  };

  const handleTicketPricingInputChange = (e) => {
    const value = e.target.value;

    // Allow numbers and decimals, but validate range 0-100
    if (/^\d*\.?\d*$/.test(value)) {
      const numValue = parseFloat(value);
      // Allow empty, or values between 0 and 100
      if (value === "" || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
        setTicketPricingInputValue(value);
      }
    }
  };

  const handleEdit = () => {
    setIsEdit(!isEdit);

    if (isEdit) {
      updatePrice();
    }
  };

  const handleVendorEdit = () => {
    setIsVendorEdit(!isVendorEdit);

    if (isVendorEdit) {
      updateVendorBoostPrice();
    }
  };

  const handleTicketEdit = () => {
    setIsTicketEdit(!isTicketEdit);

    if (isTicketEdit) {
      onSaveCommission();
    }
  };


  const handleAdminFeeChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAdminFeeInputValue(value);
    }
  };
  // Admin fee state (ADD THIS)
  const [adminFeeInputValue, setAdminFeeInputValue] = useState(
    tabCache.ticketCommission.data?.admin_fee || ""
  );


  const getServiceFee = async () => {
    const isFirstLoad = tabCache.ticketCommission.firstLoad;

    if (isFirstLoad) {
      setIsTicketLoading("list");
    }

    try {
      const response = await ServiceFeeService.getServiceFee();

      // Try to be very defensive about the possible response shapes
      let percentage =
        response?.data?.percentage ??
        response?.data?.data?.percentage ??
        response?.data?.serviceFee?.percentage ??
        null;

      let admin_fee =
        response?.data?.adminPercentage ??
        response?.data?.admin_fee ??
        response?.data?.adminFee ??
        response?.data?.data?.adminPercentage ??
        response?.data?.data?.admin_fee ??
        response?.data?.data?.adminFee ??
        response?.data?.serviceFee?.adminPercentage ??
        response?.data?.serviceFee?.admin_fee ??
        response?.data?.serviceFee?.adminFee ??
        null;

      // If backend sends an object (e.g. { amount: 3.99 }), normalise it
      if (percentage && typeof percentage === "object") {
        percentage =
          percentage.percentage ??
          percentage.amount ??
          percentage.value ??
          null;
      }

      if (admin_fee && typeof admin_fee === "object") {
        admin_fee =
          admin_fee.percentage ??
          admin_fee.amount ??
          admin_fee.value ??
          null;
      }

      // ✅ update UI (fall back to empty string if null/undefined)
      setTicketPricingInputValue(
        percentage !== null && percentage !== undefined ? String(percentage) : ""
      );
      setAdminFeeInputValue(
        admin_fee !== null && admin_fee !== undefined ? String(admin_fee) : ""
      );

      setServiceFeeExists(true);

      // ✅ update cache
      tabCache.ticketCommission.data = { percentage, admin_fee };
      tabCache.ticketCommission.firstLoad = false;
    } catch (error) {
      if (error.response?.status === 404) {
        setTicketPricingInputValue("");
        setAdminFeeInputValue("");
        setServiceFeeExists(false);

        tabCache.ticketCommission.data = null;
        tabCache.ticketCommission.firstLoad = false;
      }
    } finally {
      if (isFirstLoad) {
        setIsTicketLoading("");
      }
    }
  };


  const onSaveCommission = async () => {
    const percentage = parseFloat(ticketPricingInputValue);
    const admin_fee = parseFloat(adminFeeInputValue);

    if (
      isNaN(percentage) ||
      percentage < 0 ||
      percentage > 100 ||
      isNaN(admin_fee) ||
      admin_fee < 0
    ) {
      Toast("error", "Invalid commission or admin fee");
      setIsTicketEdit(false);
      return;
    }

    setIsTicketLoading("update");

    try {
      const payload = { percentage, adminPercentage: admin_fee };

      if (serviceFeeExists) {
        await ServiceFeeService.updateServiceFee(payload);
      } else {
        await ServiceFeeService.createServiceFee(payload);
        setServiceFeeExists(true);
      }

      tabCache.ticketCommission.data = { percentage, admin_fee };
      tabCache.ticketCommission.firstLoad = false;

      Toast("success", "Commission updated successfully!");
      setIsTicketEdit(false);
    } catch (e) {
      Toast("error", e.response?.data?.message);
    } finally {
      setIsTicketLoading("");
    }
  };


  const getPrice = async () => {
    const isFirstLoad = tabCache.boostPricing.firstLoad;

    try {
      const response = await PriceService.getPrice();
      const result = response?.data?.products;
      const amount = result?.amount || "";

      // Update state and cache
      setSearchInputValue(amount);
      tabCache.boostPricing.data = { amount };

      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.boostPricing.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      if (isFirstLoad) {
        tabCache.boostPricing.firstLoad = false;
      }
    } finally {
      if (isFirstLoad) {
        setIsLoading("");
      }
    }
  };

  const updatePrice = async () => {
    setIsLoading("update");

    try {
      const body = {
        amount: Number(searchInputValue),
      };

      await PriceService.updatePrice(body);
      Toast("success", "Amount updated Successfully!");
      await getPrice();

      setIsEdit(false);
    } catch (error) {
      Toast("error", error.response?.data?.message);
    } finally {
      setIsLoading("");
    }
  };


  const getVendorBoostPrice = async () => {
    const isFirstLoad = tabCache.vendorBoostPricing.firstLoad;

    try {
      const response = await PriceService.getVendorBoostPrice();
      const result = response?.data?.price;
      const amount = result?.amount || "";

      // Update state and cache
      setVendorBoostInputValue(amount);
      tabCache.vendorBoostPricing.data = { amount };

      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.vendorBoostPricing.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      if (isFirstLoad) {
        tabCache.vendorBoostPricing.firstLoad = false;
      }
    } finally {
      if (isFirstLoad) {
        setIsVendorLoading("");
      }
    }
  };

  const updateVendorBoostPrice = async () => {
    setIsVendorLoading("update");

    try {
      const body = {
        amount: Number(vendorBoostInputValue),
      };

      await PriceService.updateVendorBoostPrice(body);
      Toast("success", "Vendor Boost Amount updated Successfully!");
      await getVendorBoostPrice();

      setIsVendorEdit(false);
    } catch (error) {
      Toast("error", error.response?.data?.message);
    } finally {
      setIsVendorLoading("");
    }
  };



  useEffect(() => {
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.boostPricing.data) {
      // Data already shown from initial state, just fetch in background
      getPrice();
    } else {
      // No cached data, fetch and show loader
      getPrice();
    }

    // If we have cached data, show it immediately and fetch in background
    if (tabCache.vendorBoostPricing.data) {
      // Data already shown from initial state, just fetch in background
      getVendorBoostPrice();
    } else {
      // No cached data, fetch and show loader
      getVendorBoostPrice();
    }

    // Load service fee on component mount
    getServiceFee();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="mt-3 grid h-full sm:grid-cols-1  lg:grid-cols-2 gap-5">
      {/* Event Boost Pricing Section */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full p-6 overflow-x-auto"}>
          {isLoading === "list" ? (
            <div className="py-4 text-center">
              <img
                src={Loader}
                alt="Loading..."
                className="mx-auto h-16 w-16"
              />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-2">
                  Event Boost Pricing
                </h3>
                <div className="flex text-lg">
                  Set and update the per-day pricing for event boosts. Adjust the
                  boost amount to control visibility and reach for promoted
                  events.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="mt-0  flex w-full items-end justify-between gap-2">
                  <div>
                    <p className="text-lg font-medium text-white mb-2">Per Day</p>
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center rounded-lg py-1.5 text-white xl:w-36 ${!isEdit ? "bg-navy-900" : "bg-navy-900"}`}
                        onDoubleClick={handleEdit}
                      >
                        <p className="pl-3 pr-2 text-xl">$</p>
                        <input
                          ref={inputRef}
                          type="text"
                          placeholder="Price"
                          className={`block w-full bg-navy-900 text-base font-medium text-white outline-none placeholder:!text-gray-600 disabled:bg-navy-900 ${!isEdit ? "pointer-events-none" : ""}`}
                          onChange={handleSearchInputChange}

                          value={searchInputValue}
                          disabled={!isEdit}
                        />
                      </div>
                    </div>
                    {searchInputValue < 0.5 && isEdit && (
                      <div className="mt-1">
                        <p className="text-sm text-red-500">
                          Please enter an amount greater than $0.50
                        </p>
                      </div>
                    )}
                  </div>

                  <button
                    className={`flex h-[36px] w-24 items-center justify-center rounded-md  px-5 py-1.5 text-left text-base font-bold ${searchInputValue < 0.5 && isEdit
                      ? "bg-primary/50"
                      : "bg-[#F44C1D]"
                      } text-white duration-300`}
                    onClick={handleEdit}
                    disabled={searchInputValue < 0.5 && isEdit}
                  >
                    {isLoading === "update" ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      <>{isEdit ? "Update" : "Edit"}</>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Vendor Boost Pricing Section */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full p-6 overflow-x-auto"}>
          {isVendorLoading === "list" ? (
            <div className="py-4 text-center">
              <img
                src={Loader}
                alt="Loading..."
                className="mx-auto h-16 w-16"
              />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-2">
                  Vendor Boost Pricing
                </h3>
                <div className="flex text-lg">
                  Set and update the per-day pricing for vendor boosts. Adjust the
                  boost amount to control visibility and reach for promoted
                  vendors and their products.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="mt-0 flex w-full items-end justify-between gap-2">
                  <div>
                    <p className="text-lg font-medium text-white mb-2">Per Day</p>
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center rounded-lg py-1.5 text-white xl:w-36 ${!isVendorEdit ? "bg-navy-900" : "bg-navy-900"}`}
                        onDoubleClick={handleVendorEdit}
                      >
                        <p className="pl-3 pr-2 text-xl">$</p>
                        <input
                          ref={vendorInputRef}
                          type="text"
                          placeholder="Price"
                          className={`block w-full bg-navy-900 text-base font-medium text-white outline-none placeholder:!text-gray-600 disabled:bg-navy-900 ${!isVendorEdit ? "pointer-events-none" : ""}`}
                          onChange={handleVendorBoostInputChange}
                          value={vendorBoostInputValue}
                          disabled={!isVendorEdit}
                        />
                      </div>
                    </div>
                    {vendorBoostInputValue < 0.5 && isVendorEdit && (
                      <div className="mt-1">
                        <p className="text-sm text-red-500">
                          Please enter an amount greater than $0.50
                        </p>
                      </div>
                    )}
                  </div>

                  <button
                    className={`flex h-[36px] w-24  items-center justify-center rounded-md  px-5 py-1.5 text-left text-base font-bold ${vendorBoostInputValue < 0.5 && isVendorEdit
                      ? ""
                      : "bg-[#F44C1D]"
                      } text-white duration-300`}
                    onClick={handleVendorEdit}
                    disabled={vendorBoostInputValue < 0.5 && isVendorEdit}
                  >
                    {isVendorLoading === "update" ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      <>{isVendorEdit ? "Update" : "Edit"}</>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Ticket Pricing Section */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full p-6 overflow-x-auto"}>
          {isTicketLoading === "list" ? (
            <div className="py-4 text-center">
              <img
                src={Loader}
                alt="Loading..."
                className="mx-auto h-16 w-16"
              />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-2">
                  Ticket Fees
                </h3>
                <div className="flex text-lg">
                  Set and manage ticket fees,
                  including both the service fee percentage and the fixed admin fee.
                  These fees determine the total charges applied to each ticket sale.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="mt-1 flex w-full items-end justify-between gap-2">
                  <div>
                    {/* <p className="pl-3 pr-2 -mt-5 mb-2 -ml-3 text-xl">Per Ticket</p> */}
                    <div className="flex flex-wrap items-center gap-4">

                      {/* Commission Percentage */}

                      <div className="flex flex-col gap-2">
                        <p className="text-lg font-medium text-white">Service Fee</p>
                        <div
                          className="flex items-center rounded-lg bg-navy-900 py-1.5 text-white xl:w-36"
                          onDoubleClick={handleTicketEdit}
                        >
                          <p className="pl-3 pr-2 text-xl">%</p>
                          <input
                            ref={ticketInputRef}
                            type="text"
                            placeholder="Commission"
                            className={`block w-full bg-navy-900 text-base font-medium text-white outline-none placeholder:!text-gray-600 ${!isTicketEdit ? "pointer-events-none" : ""}`}
                            onChange={handleTicketPricingInputChange}
                            value={ticketPricingInputValue}
                            disabled={!isTicketEdit}
                          />
                        </div>
                      </div>

                      {/* Admin Fee */}
                      <div className="flex flex-col gap-2">
                        <p className="text-lg font-medium text-white">Admin Fee</p>
                        <div
                          className="flex items-center rounded-lg bg-navy-900 py-1.5 text-white xl:w-36"
                          onDoubleClick={handleTicketEdit}
                        >
                          <p className="pl-3 pr-2 text-xl">$</p>
                          <input
                            type="text"
                            placeholder="Admin Fee"
                            className={`block w-full bg-navy-900 text-base font-medium text-white outline-none placeholder:!text-gray-600 ${!isTicketEdit ? "pointer-events-none" : ""}`}
                            onChange={handleAdminFeeChange}
                            value={adminFeeInputValue}
                            disabled={!isTicketEdit}
                          />
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Edit / Update Button */}
                  <button
                    className={`flex h-[36px] w-24 items-center justify-center rounded-md px-5 py-1.5 text-base font-bold ${isTicketEdit &&
                      (ticketPricingInputValue === "" ||
                        adminFeeInputValue === "" ||
                        parseFloat(ticketPricingInputValue) < 0 ||
                        parseFloat(ticketPricingInputValue) > 100 ||
                        parseFloat(adminFeeInputValue) < 0)
                      ? "bg-primary/50"
                      : "bg-[#F44C1D]"
                      } text-white`}
                    onClick={handleTicketEdit}
                    disabled={
                      isTicketEdit &&
                      (ticketPricingInputValue === "" ||
                        adminFeeInputValue === "" ||
                        parseFloat(ticketPricingInputValue) < 0 ||
                        parseFloat(ticketPricingInputValue) > 100 ||
                        parseFloat(adminFeeInputValue) < 0)
                    }
                  >
                    {isTicketLoading === "update" ? (
                      <img src={Loader} alt="Loading..." className="mx-auto h-6 w-6" />
                    ) : (
                      <>{isTicketEdit ? "Update" : "Edit"}</>
                    )}
                  </button>
                </div>
              </div>

            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BoostPricing;
